import React, { useState, useEffect } from 'react';
import PageHeaderActionsBar from './ui/PageHeaderActionsBar';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Grid,
  Button,
} from '@mui/material';
import {
  Search,
  Download,
  Visibility,
  Receipt,
} from '@mui/icons-material';
import api from '../api';

const BillingExpenses = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [apiAvailable, setApiAvailable] = useState(false);

  const fetchExpenses = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/billing/expenses/');
      const list = response.data?.expenses ?? response.data ?? [];
      setExpenses(Array.isArray(list) ? list : []);
      setApiAvailable(true);
    } catch (err) {
      setExpenses([]);
      setApiAvailable(false);
      if (err.response?.status === 403) {
        setError('No access to expenses.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, []);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved': return 'success';
      case 'Pending': return 'warning';
      case 'Rejected': return 'error';
      default: return 'default';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Office': return 'primary';
      case 'Software': return 'info';
      case 'Travel': return 'warning';
      case 'Marketing': return 'secondary';
      default: return 'default';
    }
  };

  const filteredExpenses = expenses.filter(expense => {
    const desc = (expense.description || '').toLowerCase();
    const vendor = (expense.vendor || '').toLowerCase();
    const search = searchTerm.toLowerCase();
    const matchesSearch = desc.includes(search) || vendor.includes(search);
    const matchesCategory = filterCategory === 'all' || expense.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const totalExpenses = filteredExpenses.reduce((sum, expense) => sum + (expense.amount ?? 0), 0);

  const handleExport = () => {
    const data = filteredExpenses;
    if (data.length === 0) return;
    const headers = ['Description', 'Category', 'Amount', 'Vendor', 'Date', 'Status'];
    const rows = data.map(e => [
      e.description || '',
      e.category || '',
      e.amount ?? 0,
      e.vendor || '',
      e.date || '',
      e.status || ''
    ]);
    const csvContent = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `expenses-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <PageHeaderActionsBar title="Expenses" description="Track and manage business expenses" />

      {!apiAvailable ? (
        <Paper sx={{ p: 6, borderRadius: 2, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
          <Receipt sx={{ fontSize: 64, color: 'action.disabled', mb: 2 }} />
          <Typography variant="h6" gutterBottom>No expenses yet</Typography>
          <Typography variant="body2" color="text.secondary">
            Expense tracking will be available in a future release.
          </Typography>
        </Paper>
      ) : (
        <>
          <Paper sx={{ p: 2, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search expenses..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  select
                  label="Category"
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  SelectProps={{ native: true }}
                >
                  <option value="all">All Categories</option>
                  <option value="Office">Office</option>
                  <option value="Software">Software</option>
                  <option value="Travel">Travel</option>
                  <option value="Marketing">Marketing</option>
                </TextField>
              </Grid>
              <Grid item xs={12} md={3}>
                <Button variant="outlined" startIcon={<Download />} onClick={handleExport} disabled={filteredExpenses.length === 0} fullWidth>
                  Export
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Category</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Vendor</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredExpenses.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 8, textAlign: 'center' }}>
                        <Receipt sx={{ fontSize: 48, color: 'action.disabled', mb: 2, display: 'block', mx: 'auto' }} />
                        <Typography variant="body1" color="text.secondary" gutterBottom>No expenses yet</Typography>
                        <Typography variant="body2" color="text.secondary">Track business expenses and attach receipts</Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredExpenses.map((expense) => (
                    <TableRow key={expense.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {expense.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={expense.category} color={getCategoryColor(expense.category)} size="small" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          ${(expense.amount ?? 0).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>{expense.vendor}</TableCell>
                      <TableCell>{expense.date}</TableCell>
                      <TableCell>
                        <Chip label={expense.status} color={getStatusColor(expense.status)} size="small" />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" title="View Receipt" disabled>
                          <Receipt />
                        </IconButton>
                        <IconButton size="small" title="View Details" disabled>
                          <Visibility />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default BillingExpenses;
